import React from "react";
import { useNavigate } from "react-router-dom";
import useCart from "../hooks/useCart";
import useAuth from "../hooks/useAuth";
import { formatMaterialDisplayLabel } from "../utils/materialLabels";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";
import { calculateShippingPrice } from "../utils/orderPricing";
import { calculateAutomaticCustomerPrice, formatInr } from "../utils/pricing";
import { API_BASE } from "../config/api";
import "./CartPage.css";
import TermsAndConditions from "./TermsAndConditions.jsx";

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getTotalPrice, clearCart, syncCartProducts } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [shippingAddressChoice, setShippingAddressChoice] = React.useState("");
  const [shippingAddressError, setShippingAddressError] = React.useState("");
  const [shippingAddressForm, setShippingAddressForm] = React.useState({
    fullName: "",
    phone: "",
    addressLine1: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [savedDefaultAddress, setSavedDefaultAddress] = React.useState(null);
  const getStockQuantity = (product = {}) => {
    const parsedStockQuantity = Number(product?.stockQuantity);
    return Number.isFinite(parsedStockQuantity) ? Math.max(0, Math.floor(parsedStockQuantity)) : 0;
  };
  const getSelectableSizes = (product = {}) => (
    Array.isArray(product?.availableSizes)
      ? product.availableSizes
        .map((size) => String(size || '').trim())
        .filter((size) => {
          const normalizedSize = String(size || '').trim().toUpperCase();
          return Boolean(normalizedSize && normalizedSize !== 'NA' && normalizedSize !== 'N/A');
        })
      : []
  );
  const getCartItemSizeDisplay = (item = {}) => {
    const selectedSize = String(item.selectedSize || '').trim();
    if (selectedSize) {
      return selectedSize;
    }

    const selectableSizes = getSelectableSizes(item.product);
    if (selectableSizes.length === 1) {
      return selectableSizes[0];
    }

    return selectableSizes.length > 1 ? '' : 'NA';
  };
  const hasInvalidStockInCart = cart.some((item) => {
    const stockQuantity = getStockQuantity(item.product);
    return stockQuantity <= 0 || item.quantity > stockQuantity;
  });
  const userPermanentAddress = user?.permanentAddress || null;
  const permanentAddress = userPermanentAddress?.addressLine1 ? userPermanentAddress : savedDefaultAddress;
  const hasPermanentAddress = Boolean(
    permanentAddress?.addressLine1
    && permanentAddress?.city
    && permanentAddress?.state
    && permanentAddress?.pincode
  );
  const buildPermanentShippingAddress = () => ({
    fullName: permanentAddress?.fullName || user?.name || "",
    phone: permanentAddress?.phone || user?.phone || "",
    addressLine1: permanentAddress?.addressLine1 || "",
    addressLine2: permanentAddress?.addressLine2 || "",
    city: permanentAddress?.city || "",
    state: permanentAddress?.state || "",
    pincode: permanentAddress?.pincode || "",
    country: permanentAddress?.country || "India",
    isDefault: true,
  });
  const validateShippingForm = () => {
    const requiredFields = ["fullName", "phone", "addressLine1", "city", "state", "pincode", "country"];
    const hasMissingField = requiredFields.some((field) => !String(shippingAddressForm[field] || "").trim());
    if (hasMissingField) return "Please fill all shipping address fields.";
    if (!/^\d{10}$/.test(shippingAddressForm.phone)) return "Please enter a valid 10-digit phone number.";
    if (!/^\d{6}$/.test(shippingAddressForm.pincode)) return "Please enter a valid 6-digit pincode.";
    return "";
  };

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  React.useEffect(() => {
    if (user) {
      syncCartProducts();
    }
  }, [user, syncCartProducts]);

  React.useEffect(() => {
    if (!user) {
      setSavedDefaultAddress(null);
      return undefined;
    }

    let isMounted = true;

    fetch(`${API_BASE}/addresses`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((addresses) => {
        if (!isMounted || !Array.isArray(addresses)) return;
        setSavedDefaultAddress(addresses.find((address) => address.isDefault) || addresses[0] || null);
      })
      .catch(() => {
        if (isMounted) setSavedDefaultAddress(null);
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  React.useEffect(() => {
    if (!user) {
      return undefined;
    }

    return bindStorefrontRefresh(() => {
      syncCartProducts();
    }, { scopes: ["products"] });
  }, [user, syncCartProducts]);

  const handleQuantityChange = (productId, newQuantity, selectedSize = '') => {
    updateQuantity(productId, parseInt(newQuantity), selectedSize);
  };

  const handleRemoveItem = (productId, selectedSize = '') => {
    removeFromCart(productId, selectedSize);
  };

  const handleCheckout = () => {
    if (hasInvalidStockInCart) {
      window.alert("One or more items in your cart are out of stock or exceed the available quantity.");
      return;
    }
    if (!shippingAddressChoice) {
      setShippingAddressError("Please select whether your permanent address is your shipping address.");
      return;
    }
    if (shippingAddressChoice === "yes") {
      if (!hasPermanentAddress) {
        setShippingAddressError("Please add your Permanent Address in My Account before using it for shipping.");
        return;
      }
      sessionStorage.setItem("checkoutShippingAddress", JSON.stringify(buildPermanentShippingAddress()));
    }
    if (shippingAddressChoice === "no") {
      const validationError = validateShippingForm();
      if (validationError) {
        setShippingAddressError(validationError);
        return;
      }
      sessionStorage.setItem(
        "checkoutShippingAddress",
        JSON.stringify({
          ...shippingAddressForm,
          addressLine2: "",
          isDefault: false,
        })
      );
    }

    setShowTermsModal(true);
  };
  const handleAcceptTerms = () => {
    setShowTermsModal(false);
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate("/store");
  };

  if (loading) {
    return <div className="cart-loading">Loading...</div>;
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }

  const cartSubtotal = getTotalPrice();
  const shippingPrice = calculateShippingPrice(cartSubtotal);
  const cartTotal = cartSubtotal + shippingPrice;

  return (
    <div className="cart-page-container">
      {showTermsModal && (
        <div className="terms-modal-overlay">
          <div className="terms-modal-shell">
            <button
              type="button"
              className="terms-modal-close"
              aria-label="Close terms and conditions"
              onClick={() => setShowTermsModal(false)}
            >
              &times;
            </button>
            <TermsAndConditions onDone={handleAcceptTerms} />
          </div>
        </div>
      )}
      <h1>Your Shopping Cart</h1>

      {cart.length === 0 ? (
        <div className="empty-cart">
          <p>Your cart is empty</p>
          <button onClick={handleContinueShopping} className="continue-shopping">
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="cart-content">
          <div className="cart-items">
            <div className="cart-header">
              <span className="header-product">Product</span>
              <span className="header-price">Price</span>
              <span className="header-quantity">Quantity</span>
              <span className="header-total">Total</span>
              <span className="header-action">Action</span>
            </div>

            {cart.map((item) => (
              <div key={`${item.product._id}-${item.selectedSize || 'default'}`} className="cart-item">
                <div className="item-product">
                  <img src={item.product.images[0]} alt={item.product.title} />
                  <div className="item-details">
                    <h3>{item.product.title}</h3>
                    <p>{formatMaterialDisplayLabel(item.product.material)}</p>
                    {getCartItemSizeDisplay(item) ? <p>Size: {getCartItemSizeDisplay(item)}</p> : null}
                    {getStockQuantity(item.product) <= 0 ? (
                      <p style={{ color: "#b23d3d", fontWeight: 600 }}>Out of stock</p>
                    ) : item.quantity > getStockQuantity(item.product) ? (
                      <p style={{ color: "#b23d3d", fontWeight: 600 }}>
                        Only {getStockQuantity(item.product)} item(s) available. Please reduce quantity.
                      </p>
                    ) : getStockQuantity(item.product) <= 5 ? (
                      <p style={{ color: "#9a5d00", fontWeight: 600 }}>
                        Only {getStockQuantity(item.product)} item(s) left in stock.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="item-price">{formatInr(calculateAutomaticCustomerPrice(item.product.price))}</div>

                <div className="item-quantity">
                  <button
                    onClick={() => handleQuantityChange(item.product._id, item.quantity - 1, item.selectedSize)}
                    className="quantity-btn"
                    disabled={item.quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={getStockQuantity(item.product) > 0 ? getStockQuantity(item.product) : undefined}
                    value={item.quantity}
                    onChange={(e) =>
                      handleQuantityChange(item.product._id, e.target.value, item.selectedSize)
                    }
                  />
                  <button
                    onClick={() => handleQuantityChange(item.product._id, item.quantity + 1, item.selectedSize)}
                    className="quantity-btn"
                    disabled={getStockQuantity(item.product) <= 0 || item.quantity >= getStockQuantity(item.product)}
                  >
                    +
                  </button>
                </div>

                <div className="item-total">
                  â{formatInr(calculateAutomaticCustomerPrice(item.product.price) * item.quantity)}
                </div>

                <div className="item-action">
                  <button
                    onClick={() => handleRemoveItem(item.product._id, item.selectedSize)}
                    className="remove-btn"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{`\u20B9${cartSubtotal.toFixed(2)}`}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>FREE</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{`\u20B9${cartTotal.toFixed(2)}`}</span>
            </div>

            <div className="coupon-code">
              <input type="text" placeholder="Enter coupon code" />
              <button>Apply</button>
            </div>

            <div className="shipping-choice-panel">
              <h3>Is this your Shipping Address?</h3>
              <div className="shipping-choice-options">
                <label>
                  <input
                    type="radio"
                    name="shippingAddressChoice"
                    value="yes"
                    checked={shippingAddressChoice === "yes"}
                    onChange={() => {
                      setShippingAddressChoice("yes");
                      setShippingAddressError("");
                    }}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="shippingAddressChoice"
                    value="no"
                    checked={shippingAddressChoice === "no"}
                    onChange={() => {
                      setShippingAddressChoice("no");
                      setShippingAddressError("");
                    }}
                  />
                  No
                </label>
              </div>
              {shippingAddressChoice === "yes" && (
                <div className="shipping-address-preview">
                  {hasPermanentAddress ? (
                    <>
                      <strong>{buildPermanentShippingAddress().fullName}</strong>
                      <span>{buildPermanentShippingAddress().addressLine1}</span>
                      <span>{buildPermanentShippingAddress().city}, {buildPermanentShippingAddress().state} - {buildPermanentShippingAddress().pincode}</span>
                      <span>{buildPermanentShippingAddress().country}</span>
                      <span>Phone: {buildPermanentShippingAddress().phone}</span>
                    </>
                  ) : (
                    <span>Please add your Permanent Address in My Account.</span>
                  )}
                </div>
              )}
              {shippingAddressChoice === "no" && (
                <div className="shipping-address-form">
                  {[
                    ["fullName", "Full Name"],
                    ["phone", "Phone Number"],
                    ["addressLine1", "Address"],
                    ["city", "City"],
                    ["state", "State"],
                    ["pincode", "Pincode"],
                    ["country", "Country"],
                  ].map(([field, label]) => (
                    <input
                      key={field}
                      type="text"
                      placeholder={label}
                      value={shippingAddressForm[field]}
                      maxLength={field === "phone" ? 10 : field === "pincode" ? 6 : undefined}
                      onChange={(event) => {
                        const value = field === "phone" || field === "pincode"
                          ? event.target.value.replace(/\D/g, "")
                          : event.target.value;
                        setShippingAddressForm((form) => ({ ...form, [field]: value }));
                        setShippingAddressError("");
                      }}
                    />
                  ))}
                </div>
              )}
              {shippingAddressError && <p className="shipping-choice-error">{shippingAddressError}</p>}
            </div>

            <button onClick={handleCheckout} className="checkout-btn" disabled={hasInvalidStockInCart || !shippingAddressChoice}>
              Proceed to Checkout
            </button>

            <div className="cart-actions">
              <button onClick={handleContinueShopping} className="continue-shopping">
                Continue Shopping
              </button>
              <button onClick={clearCart} className="clear-cart">
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;

